import { useMemo } from 'react';

export const mentionRegex = /@\{ref:user::id:(\d+)::name:([^}]+)\}/g;

/**
 * Pure form of usePlainTextMentions, for selectors and other non-React callers.
 * Passes null/undefined through unchanged so callers keep their `title || summary`
 * fallbacks. Uses String#replace, which resets the shared /g regex's lastIndex —
 * never call `.test()` on `mentionRegex`.
 */
export function flattenMentions<T extends string | null | undefined>(
  text: T
): T {
  if (typeof text !== 'string') return text;
  return text.replace(
    mentionRegex,
    (_match, _userId, userName) => `@${userName}`
  ) as T;
}

interface UsePlainTextMentionsProps {
  text: string;
  disabled?: boolean;
}

export const usePlainTextMentions = ({
  text,
  disabled = false,
}: UsePlainTextMentionsProps): string => {
  return useMemo(() => {
    if (disabled) {
      return text; // If disabled, return the text as-is without transformation
    }

    return flattenMentions(text); // Replace each mention with just the username
  }, [text, disabled]); // Include disabled in the dependency array
};
